<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-work-efficiency">
    <nx-report-header title="作业效能分析看板">
      <div class="kb-header-actions">
        <el-select v-model="period" size="small" style="width: 120px" @change="loadData">
          <el-option label="今日" value="today" />
          <el-option label="本周" value="week" />
          <el-option label="本月" value="month" />
        </el-select>
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <div class="kb-kpi-row cols-4">
      <div v-for="(k, i) in kpis" :key="i" class="kb-kpi-card">
        <div class="kb-kpi-label">{{ k.label }}</div>
        <div class="kb-kpi-value">{{ k.value }}</div>
        <div class="kb-kpi-sub">{{ k.sub }}</div>
      </div>
    </div>

    <div class="kb-charts-row">
      <div class="kb-chart-card">
        <div class="kb-chart-head"><span>翻车机效能</span></div>
        <div ref="c1" class="kb-chart-box tall" />
      </div>
      <div class="kb-chart-card">
        <div class="kb-chart-head"><span>班次效率</span></div>
        <div ref="c2" class="kb-chart-box tall" />
      </div>
    </div>

    <div class="kb-charts-row">
      <div class="kb-chart-card">
        <div class="kb-chart-head">设备状态时间分布</div>
        <div ref="c3" class="kb-chart-box" />
      </div>
      <div class="kb-chart-card">
        <div class="kb-chart-head">翻卸作业时长趋势</div>
        <div ref="c4" class="kb-chart-box" />
      </div>
    </div>

    <div class="kb-chart-card">
      <div class="kb-chart-head">故障类型统计</div>
      <div ref="c5" class="kb-chart-box" style="height: 260px" />
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchWorkEfficiency } from './api'

function sum(list) {
  return Array.isArray(list) ? list.reduce((total, item) => total + Number(item || 0), 0) : 0
}

function average(list) {
  return Array.isArray(list) && list.length ? sum(list) / list.length : 0
}

function formatNumber(value, digits = 1) {
  const n = Number(value)
  return isFinite(n) ? n.toFixed(digits) : (0).toFixed(digits)
}

export default {
  name: 'KanbanWorkEfficiency',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      period: 'week',
      payload: null,
      kpis: [],
      chartInstances: []
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.chartInstances.forEach((c) => c && c.dispose())
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        this.payload = await fetchWorkEfficiency({ period: this.period })
        this.buildKpis()
        this.$nextTick(() => this.renderAll())
      } catch (e) {
        this.payload = null
        this.kpis = []
        this.clearCharts()
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    buildKpis() {
      const p = this.payload
      if (!p) return
      const summary = p.kpiSummary
      const deviceStatus = p.deviceStatus || {}
      const running = Number(deviceStatus.running || 0)
      const standby = Number(deviceStatus.standby || 0)
      const fault = Number(deviceStatus.fault || 0)
      const totalHours = running + standby + fault
      const avgActualCapacity = average(p.dumperData && p.dumperData.actualCapacity)
      const avgTheoreticalCapacity = average(p.dumperData && p.dumperData.theoreticalCapacity)
      const fallbackCapacityRate = avgTheoreticalCapacity ? (avgActualCapacity / avgTheoreticalCapacity) * 100 : 0
      const fallbackAvgDuration = average(p.durationTrend && p.durationTrend.dumperDuration)
      const fallbackUtilizationRate = totalHours ? (running / totalHours) * 100 : 0

      const capacityRate = summary ? summary.capacityRate : fallbackCapacityRate
      const avgDuration = summary ? summary.avgDurationMinutes : fallbackAvgDuration
      const utilizationRate = summary ? summary.utilizationRate : fallbackUtilizationRate
      const runningHours = summary ? summary.runningHours : running
      const kpiTotalHours = summary ? summary.totalHours : totalHours
      const faultHours = summary ? summary.faultHours : fault
      this.kpis = [
        { label: '翻车机产能达成率', value: `${formatNumber(capacityRate)}%`, sub: summary ? summary.capacityCompareText : '' },
        { label: '平均翻卸时长', value: `${formatNumber(avgDuration)} 分钟/车`, sub: '' },
        { label: '翻车机利用率', value: `${formatNumber(utilizationRate)}%`, sub: `运行 ${formatNumber(runningHours)}h / 总 ${formatNumber(kpiTotalHours)}h` },
        { label: '故障停机时长', value: `${formatNumber(faultHours)} 小时`, sub: '' }
      ]
    },
    onResize() {
      this.chartInstances.forEach((c) => c && c.resize())
    },
    clearCharts() {
      this.chartInstances.forEach((c) => c && c.dispose())
      this.chartInstances = []
    },
    renderAll() {
      this.clearCharts()
      const p = this.payload
      if (!p) return
      const refs = [this.$refs.c1, this.$refs.c2, this.$refs.c3, this.$refs.c4, this.$refs.c5]
      refs.forEach((el) => {
        if (el) this.chartInstances.push(echarts.init(el))
      })
      const [ch1, ch2, ch3, ch4, ch5] = this.chartInstances
      if (!ch1 || !ch2 || !ch3 || !ch4 || !ch5) return
      ch1.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['实际产能', '理论产能'], textStyle: { color: '#334155' } },
        xAxis: { type: 'category', data: p.dumperData.dates },
        yAxis: { type: 'value', name: '车厢/天' },
        series: [
          { name: '实际产能', type: 'line', data: p.dumperData.actualCapacity, smooth: true, lineStyle: { color: '#3b82f6', width: 3 } },
          { name: '理论产能', type: 'line', data: p.dumperData.theoreticalCapacity, lineStyle: { color: '#94a3b8', type: 'dashed' } }
        ]
      })
      ch2.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['实际效率', '目标效率'] },
        xAxis: { type: 'category', data: p.shiftData.shifts, axisLabel: { rotate: 15 } },
        yAxis: { type: 'value', max: 100, name: '%' },
        series: [
          { name: '实际效率', type: 'bar', data: p.shiftData.efficiencies, itemStyle: { color: '#f97316', borderRadius: [6, 6, 0, 0] } },
          { name: '目标效率', type: 'line', data: p.shiftData.targets, lineStyle: { color: '#ef4444', type: 'dashed' } }
        ]
      })
      ch3.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {d}% ({c}h)' },
        series: [
          {
            type: 'pie',
            radius: '55%',
            data: [
              { name: '运行', value: p.deviceStatus.running, itemStyle: { color: '#10b981' } },
              { name: '待机', value: p.deviceStatus.standby, itemStyle: { color: '#eab308' } },
              { name: '故障', value: p.deviceStatus.fault, itemStyle: { color: '#ef4444' } }
            ]
          }
        ]
      })
      ch4.setOption({
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: p.durationTrend.dates },
        yAxis: { type: 'value', name: '分钟/车' },
        series: [{ type: 'line', data: p.durationTrend.dumperDuration, smooth: true, areaStyle: { opacity: 0.08 } }]
      })
      ch5.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        xAxis: { type: 'category', data: p.faultTypes.types, axisLabel: { rotate: 30 } },
        yAxis: { type: 'value', name: '次数' },
        series: [{ type: 'bar', data: p.faultTypes.counts, itemStyle: { color: '#ef4444', borderRadius: [6, 6, 0, 0] } }]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
